
import React from "react";
import { useSelector } from "react-redux";
import { motion } from "framer-motion";
import { useLocation } from "react-router-dom";
import { useIsMobile } from "@/hooks/use-mobile";
import { RootState } from "@/store";
import { Loader } from "lucide-react";
import DashboardProfileDropdown from "@/components/ProfileDropdown";
import { useAuth } from "@/hooks/useAuth";

interface LayoutProps {
  children: React.ReactNode;
}

const Layout = ({ children }: LayoutProps) => {
  const location = useLocation();
  const isMobile = useIsMobile();
  const isAdminRoute = location.pathname === "/admin";
  const isEditVideoRoute = location.pathname.includes("/edit-video/");
  const listing = useSelector((state: RootState) => state.listing);
  const user = useAuth();

  return (
    <motion.div
      className="min-h-screen flex flex-col items-center justify-center py-12 pb-20 px-4"
      style={{
        background: "transparent"
      }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
    >
      {isMobile ? (
        <div className="w-full max-w-md mx-auto bg-background rounded-xl shadow-sm">
          {children}
        </div>
      ) : (
        <motion.div
          className={`w-full mx-auto bg-background rounded-xl shadow-lg px-8 py-8 relative ${
            isAdminRoute ? 'max-w-5xl' :
            isEditVideoRoute ? 'max-w-7xl' : 'max-w-2xl'
          }`}
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.5 }}
          style={{
            boxShadow: "0 10px 30px rgba(0, 0, 0, 0.1), 0 1px 8px rgba(0, 0, 0, 0.06)",
            transform: "perspective(1000px) rotateX(2deg)",
            transformOrigin: "bottom center"
          }}
        >
          <DashboardProfileDropdown user={user.session?.user} className="absolute right-0 top-[-50px] z-10" />

          {listing.jobStatus === "processing" && (
            <section className="absolute top-2 right-2">
              <Loader className="animate-spin" />
            </section>
          )}

          {children}

          <footer className="absolute bottom-[-3.5rem] right-0 text-white text-sm text-center w-full">
            <div className="container mx-auto px-4 text-center text-white/60">
              <p>© {new Date().getFullYear()} Clips by Keven. All rights reserved.</p>
              <div className="flex justify-center space-x-6 mt-1">
                <a href="tel:8645612205" className="hover:text-white transition-colors">(864) 561-2205</a>
                <a href="mailto:support@keven.ai" className="hover:text-white transition-colors">support@keven.ai</a>
                {/* <Link to="/sales-support#contact" className="hover:text-white transition-colors">Contact</Link> */}
              </div>
            </div>
          </footer>
        </motion.div>
      )}
    </motion.div>
  );
};

export default Layout;
